// DRM Engine for handling FairPlay DRM
const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

class Logger {
  constructor(level = LogLevel.INFO) {
    this.level = level;
  }

  debug(...args) {
    if (this.level <= LogLevel.DEBUG) console.debug(...args);
  }

  info(...args) {
    if (this.level <= LogLevel.INFO) console.info(...args);
  }

  warn(...args) {
    if (this.level <= LogLevel.WARN) console.warn(...args);
  }

  error(...args) {
    if (this.level <= LogLevel.ERROR) console.error(...args);
  }
}

export class StreamDrmEngine {
  constructor(fpLicenseUrl, fpCertificateUrl, logLevel = LogLevel.INFO) {
    this.fpLicenseUrl = fpLicenseUrl;
    this.fpCertificateUrl = fpCertificateUrl;
    this.logger_ = new Logger(logLevel);
    this.fpCertificate = null;
  }

  async initFairPlay(videoEl) {
    try {
      const config = {
        keySystem: 'com.apple.fps',
        certificate: await this.getFpCertificate(),
        initDataTypes: ['cenc', 'sinf', 'skd'],
        audioCapabilities: [{
          contentType: 'application/vnd.apple.mpegurl',
          robustness: '',
        }],
        distinctiveIdentifier: 'not-allowed',
        persistentState: 'not-allowed',
        sessionTypes: ['temporary'],
      };

      const keys = await navigator.requestMediaKeySystemAccess(
        'com.apple.fps.1_0',
        [config],
      );

      this.fpCertificate = await this.getFpCertificate();
      await keys.setServerCertificate(this.fpCertificate);
      await videoEl.setMediaKeys(keys);

      // Add encrypted event listener
      videoEl.addEventListener('encrypted', (e) => this.onFpEncrypted(e));
    } catch (_e) {
      // Log error but don't rethrow - let video element handle fallback
      this.logger_.error('Failed to initialize FairPlay DRM');
    }
  }

  async onFpEncrypted(event) {
    try {
      // Debug logging
      this.logger_.debug('=== FairPlay Encrypted Event Debug ===');
      this.logger_.debug('Event:', event);
      this.logger_.debug('Event target:', event.target);
      this.logger_.debug('Event target tagName:', event.target?.tagName);
      this.logger_.debug('Init data type:', event.initDataType);
      this.logger_.debug('Init data:', event.initData);
      this.logger_.debug(
        'Init data constructor:',
        event.initData?.constructor?.name,
      );
      this.logger_.debug('Init data length:', event.initData?.byteLength);
      this.logger_.debug(
        'Init data as Uint8Array:',
        new Uint8Array(event.initData),
      );

      // Skip empty events from HLS.js
      if (!event.initDataType || !event.initData) {
        this.logger_.warn(
          '⚠️ Skipping invalid encrypted event - missing initDataType or initData',
        );
        this.logger_.warn(
          'This is likely HLS.js firing empty encrypted events despite emeEnabled: false',
        );
        return;
      }

      this.logger_.debug(
        '✅ Valid encrypted event detected - proceeding with DRM setup',
      );

      // Get init data
      const initData = event.initData;
      if (!initData) {
        this.logger_.error('No initData provided in encrypted event');
        return;
      }

      if (!(initData instanceof ArrayBuffer)) {
        this.logger_.error(
          'initData is not an ArrayBuffer:',
          initData.constructor.name,
        );
        return;
      }

      if (initData.byteLength === 0) {
        this.logger_.error('initData is empty');
        return;
      }

      this.logger_.debug('Creating session...');

      const mediaKeys = event.target.mediaKeys;
      const session = mediaKeys.createSession();
      if (!session) {
        this.logger_.error('Failed to create media key session');
        return;
      }

      this.logger_.debug('Calling generateRequest with:', {
        initDataType: event.initDataType,
        initDataLength: initData.byteLength,
      });

      await session.generateRequest(event.initDataType, initData);
      this.logger_.debug('generateRequest successful');

      // Add message handler
      session?.addEventListener('message', (e) => {
        this.handleMessage(e.target, e.message);
      });
    } catch (error) {
      this.logger_.error('FP license update failed;');
      this.logError(error);
    }
  }

  logError(e) {
    this.logger_.error('=== FairPlay Error Details ===');
    this.logger_.error('Error object:', e);
    this.logger_.error(
      'Error message:',
      e instanceof Error ? e.message : String(e),
    );
    this.logger_.error(
      'Error stack:',
      e instanceof Error ? e.stack : 'No stack',
    );
  }

  async handleMessage(session, message) {
    try {
      const response = await this.getResponse(message, this.fpLicenseUrl);
      await session.update(response);
    } catch (error) {
      this.logError(error);
    }
  }

  async getResponse(message, url) {
    const response = await fetch(url, {
      method: 'POST',
      body: message,
    });
    return response.arrayBuffer();
  }

  async getFpCertificate() {
    if (this.fpCertificate) {
      return this.fpCertificate;
    }
    const response = await fetch(this.fpCertificateUrl);
    this.fpCertificate = await response.arrayBuffer();
    return this.fpCertificate;
  }
}

StreamDrmEngine.prototype.fpLicenseUrl = '';
